'use client';

import { c as _c } from "react/compiler-runtime";
import React from 'react';
import { BodyShort, Search, ToggleGroup } from '@navikt/ds-react';
import { ToggleGroupItem } from '@navikt/ds-react/ToggleGroup';
import Resultatliste from './Resultatliste';
import styles from './ResultatWrapper.module.css';
import { store } from './store/store';
import { ArbeidsstedResponse, ArbeidstakerResponse } from '@/api/aaregDistApi';
import { settSammenNavn } from '@/utils/formatting/tekst';
import { useErKortliste } from '@/utils/mediaQueries';
import { IngenTreff } from 'components/arbeidsforholdsliste/IngenTreff';
import NavnIdentHeader from 'components/navn-ident-header/NavnIdentHeader';
import Spinner from 'components/spinner/Spinner';
import BackToTopButton from 'components/tilbake-til-toppen/BackToTopButton';
import dayjs from 'dayjs';
type Arbeidsforhold = ArbeidsstedResponse['arbeidsforhold'][0] | ArbeidstakerResponse['arbeidsforhold'][0];
const matcherPeriode = (arbeidsforhold: Arbeidsforhold, periode: string) => {
  switch (periode) {
    case 'avsluttede':
      return dayjs(arbeidsforhold.ansettelsesperiodeSlutt).isBefore(dayjs());
    case 'aktive':
      return arbeidsforhold.ansettelsesperiodeSlutt == null || dayjs(arbeidsforhold.ansettelsesperiodeSlutt).isAfter(dayjs());
    case 'fremtidige':
      return dayjs(arbeidsforhold.ansettelsesperiodeStart).isAfter(dayjs());
    default:
      return true;
  }
};
const matcherSoek = (arbeidsforhold: Arbeidsforhold, soek: string) => settSammenNavn(arbeidsforhold.arbeidstaker).toLowerCase().includes(soek.toLowerCase());
interface ResultatWrapperProps {
  loading: boolean;
  data: ArbeidsstedResponse | ArbeidstakerResponse | null;
  type: 'arbeidstaker' | 'arbeidssted' | 'samarbeidspartner_arbeidstaker' | 'samarbeidspartner_arbeidssted';
  onNavigateToArbeidsforhold?: (uuid: string) => void;
  onEndreDataelementer?: () => void;
}
const ResultatWrapper = t0 => {
  const $ = _c(22);
  const {
    loading,
    data,
    type,
    onEndreDataelementer,
    onNavigateToArbeidsforhold
  } = t0;
  const {
    trefflisteFilter,
    tekst,
    formState,
    bruker
  } = store.useReducerState();
  const erKortliste = useErKortliste();
  const [soek, setSoek] = React.useState("");
  const [periode, setPeriode] = React.useState(trefflisteFilter);
  let t1;
  if ($[0] !== data?.arbeidsforhold || $[1] !== periode || $[2] !== soek) {
    t1 = data?.arbeidsforhold?.filter(arbeidsforhold => matcherPeriode(arbeidsforhold, periode) && matcherSoek(arbeidsforhold, soek)) ?? [];
    $[0] = data?.arbeidsforhold;
    $[1] = periode;
    $[2] = soek;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const filtrerteArbeidsforhold = t1;
  let t2;
  if ($[4] !== bruker || $[5] !== data || $[6] !== formState || $[7] !== type) {
    t2 = () => {
      switch (type) {
        case "arbeidstaker":
          {
            return {};
          }
        case "arbeidssted":
          {
            return data && "arbeidssted" in data ? {
              ident: data.arbeidssted.offentligIdent
            } : {};
          }
        default:
          {
            return {
              ident: formState.ident,
              type: formState.type,
              rapporteringsordning: formState.rapporteringsordning.join(","),
              arbeidsforholdstatus: formState.arbeidsforholdstatus.join(","),
              arbeidsforholdtype: formState.arbeidsforholdtype.join(","),
              ...(bruker.valgtOrg?.orgnr && {
                organisasjonsnummer: bruker.valgtOrg.orgnr
              })
            };
          }
      }
    };
    $[4] = bruker;
    $[5] = data;
    $[6] = formState;
    $[7] = type;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const excelParametre = t2;
  let t3;
  if ($[9] !== data || $[10] !== erKortliste || $[11] !== excelParametre || $[12] !== filtrerteArbeidsforhold || $[13] !== loading || $[14] !== onEndreDataelementer || $[15] !== onNavigateToArbeidsforhold || $[16] !== periode || $[17] !== tekst || $[18] !== type) {
    t3 = loading ? <Spinner tekst={tekst.loading.henterArbeidsforhold} /> : data ? <div><NavnIdentHeader navn={"arbeidssted" in data ? data.arbeidssted.type == "organisasjon" ? data.arbeidssted.organisasjonsnavn : data.arbeidssted.type == "person" ? settSammenNavn(data.arbeidssted) : "Navn mangler" : settSammenNavn(data.arbeidstaker)} ident={"arbeidssted" in data ? data.arbeidssted.offentligIdent : data.arbeidstaker.offentligIdent} excelUtskrift={excelParametre()} endreDataElementer={type == "samarbeidspartner_arbeidssted" || type == "samarbeidspartner_arbeidstaker"} onEndreDataelementer={onEndreDataelementer} pdfUtksrift={true} printData={type === "arbeidstaker" || type === "samarbeidspartner_arbeidstaker" ? {
        type: "arbeidstaker" as const,
        data: data as ArbeidstakerResponse
      } : {
        type: "arbeidssted" as const,
        data: data as ArbeidsstedResponse
      }} /><BackToTopButton />{data.arbeidsforhold && data.arbeidsforhold.length > 0 ? <><div className={styles.filterRad}><Search label={tekst.filter.soekEtterNavn} variant="simple" size="small" hideLabel={false} htmlSize={34} className={styles.searchField} onChange={setSoek} /><ToggleGroup className={styles.statusFilter} value={periode} label={tekst.filter.periode} size="small" onChange={setPeriode}><ToggleGroupItem value="alle">{tekst.filter.alle}</ToggleGroupItem><ToggleGroupItem value="avsluttede">{tekst.filter.avsluttede}</ToggleGroupItem><ToggleGroupItem value="aktive">{tekst.filter.aktive}</ToggleGroupItem><ToggleGroupItem value="fremtidige">{tekst.filter.fremtidige}</ToggleGroupItem></ToggleGroup></div>{erKortliste && <BodyShort size="small" className={styles.antall} aria-live="polite">{tekst.filter.viserAntall.replace("%s", String(filtrerteArbeidsforhold.length)).replace("%s", String(data.arbeidsforhold.length))}</BodyShort>}<Resultatliste data={{
          ...data,
          arbeidsforhold: filtrerteArbeidsforhold
        }} onNavigateToArbeidsforhold={onNavigateToArbeidsforhold} /></> : <IngenTreff />}</div> : null;
    $[9] = data;
    $[10] = erKortliste;
    $[11] = excelParametre;
    $[12] = filtrerteArbeidsforhold;
    $[13] = loading;
    $[14] = onEndreDataelementer;
    $[15] = onNavigateToArbeidsforhold;
    $[16] = periode;
    $[17] = tekst;
    $[18] = type;
    $[19] = t3;
  } else {
    t3 = $[19];
  }
  let t4;
  if ($[20] !== t3) {
    t4 = <div>{t3}</div>;
    $[20] = t3;
    $[21] = t4;
  } else {
    t4 = $[21];
  }
  return t4;
};
export default ResultatWrapper;